<script setup lang="ts">
import type { AtcAdvertiserSuggestion } from '../../models/watchlist.types'

import { useI18n } from '@/shared/i18n'
import { Alert, Button, Icon, Input } from '@/shared/ui'

const query = defineModel<string>({ required: true })

defineProps<{
  suggestions: AtcAdvertiserSuggestion[]
  loading: boolean
  disabled: boolean
  errorKey: string | null
}>()

const emit = defineEmits<{
  add: [suggestion?: AtcAdvertiserSuggestion]
}>()

const { t } = useI18n()
</script>

<template>
  <section class="atc-search relative grid gap-xs">
    <div
      class="atc-search__controls grid grid-cols-[minmax(0,_1fr)_auto] items-start gap-sm max-sm:grid-cols-[minmax(0,_1fr)]"
    >
      <Input
        v-model="query"
        :label="t('workspaceDefinitions.watchlist.atc.searchLabel')"
        :placeholder="t('workspaceDefinitions.watchlist.atc.searchPlaceholder')"
        :disabled="disabled"
        @enter="emit('add')"
      >
        <template #start><Icon name="search" :size="16" /></template>
      </Input>
      <Button
        variant="soft"
        :disabled="disabled || query.trim().length === 0"
        data-testid="atc-add-advertiser"
        @click="emit('add')"
      >
        {{ t('workspaceDefinitions.watchlist.atc.action.add') }}
      </Button>
    </div>

    <p v-if="loading" class="atc-search__status m-0 text-muted text-[0.75rem]" role="status">
      {{ t('workspaceDefinitions.watchlist.atc.searching') }}
    </p>
    <Alert v-else-if="errorKey" tone="danger">{{ t(errorKey) }}</Alert>
    <div
      v-if="suggestions.length"
      class="atc-search__suggestions absolute z-2 inset-[calc(100%_+_var(--ui-space-2xs))_0_auto] grid max-h-56 overflow-auto border border-border rounded-md bg-surface shadow-pop"
      role="listbox"
    >
      <button
        v-for="suggestion in suggestions"
        :key="suggestion.advertiser_id"
        class="atc-search__suggestion flex items-center justify-between gap-md py-sm px-md border-b border-b-border text-fg text-start [&:hover]:bg-surface-sunken [&:focus-visible]:bg-surface-sunken"
        type="button"
        role="option"
        @click="emit('add', suggestion)"
      >
        <strong>{{ suggestion.advertiser_name }}</strong>
        <span>{{ suggestion.advertiser_id }}</span>
      </button>
    </div>
  </section>
</template>

<style scoped>
.atc-search .atc-search__suggestion span {
  color: var(--ui-color-muted);
  font-size: 0.75rem;
}
</style>
